import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
  SerializeFrom } from
"@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import { motion } from "framer-motion";
import styles from "highlight.js/styles/base16/dracula.css";
import type { BlogPosting } from "schema-dts";

import { PostBody } from "~/components/blog/post-body";
import { PostHeader } from "~/components/blog/post-header";
import { Error } from "~/components/site/error";
import { container, item, Layout } from "~/components/site/layout";
import { getTitle, RESPONSES, ROUTE_DATA, SITE } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import type { Urls } from "~/lib/get-urls";
import { getUrls } from "~/lib/get-urls";
import { getPost } from "~/lib/post";
import { readingTime } from "~/lib/reading-time";
import { required } from "~/lib/required";
import type { StructuredDataFunction } from "~/lib/structured-data";

const { path } = ROUTE_DATA.blog;

type LoaderData = Readonly<{
  post: ReturnType<typeof getPost>;
  urls: Urls;
}>;

export const links: LinksFunction = () => {
  return [{ rel: "stylesheet", href: styles }];
};

export function loader({ params, request }: LoaderFunctionArgs) {
  const post = getPost(required(params.slug, "params.slug"));
  if (post == null) throw RESPONSES.notFound();

  return {
    post,
    urls: getUrls({ path: `${path}/${post.slug}`, request })
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data) {
    if (data?.post == null) return [];

    const { image, imageUrl } = getImageData(data);
    const { post, urls } = data;

    return [
    ...getMeta({
      desc: post.description ?? post.excerpt,
      title: getTitle(post.title),
      urls
    }),
    { property: "og:image", content: imageUrl },
    { property: "og:type", content: "article" },
    {
      name: "twitter:alt",
      content: image && image.alt ? image.alt : SITE.image.alt
    },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: imageUrl }];

  } else return [];
};

const structuredData: StructuredDataFunction<
  SerializeFrom<typeof loader>,
  BlogPosting> =
({ data }) => {
  const { imageUrl } = getImageData(data);
  const { post } = data;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    datePublished: post.date,
    headline: post.title,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": post.slug
    },
    image: imageUrl,
    author: post.authors.map((a) => ({
      "@type": "Person",
      jobTitle: a.jobTitle,
      name: a.name,
      url: a.social.linkedin ?? a.social?.twitter ?? a.social.opensea
    }))
  };
};

export const handle = { structuredData };

export default function PostSlug(): JSX.Element {
  const { post, urls } = useLoaderData<LoaderData>();
  if (post == null) return <></>;

  return (
    <Layout>
      <motion.article
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="section-first blog-post">

        <div className="container">
          <motion.div variants={item} className="pb-12">
            <div className="eyebrow">Blog</div>
            <h1 className="h2 m-0">{post.title}</h1>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 items-start lg:gap-16">
            <motion.div variants={item} className="lg:sticky top-16">
              <PostHeader
                authors={post.authors}
                date={post.date}
                title={post.title}
                url={urls.envDependent}
                readingTime={readingTime(post.content).text}
                timeOverride={post.timeOverride} />

            </motion.div>
            <motion.div variants={item} className="col-span-2 mt-2">
              <PostBody
                authors={post.authors}
                coverImage={post.coverImage}
                slug={post.slug}
                discussLinkedIn={post.discussLinkedIn}
                discussTwitter={post.discussTwitter}
                discussFarcaster={post.discussFarcaster}
                callToAction={post.callToAction} />

            </motion.div>
          </div>
        </div>
      </motion.article>
    </Layout>);

}

export function CatchBoundary() {
  return <Error statusCode={404} themed />;
}

function getImageData({ post, urls }: LoaderData) {
  if (post == null) return { image: undefined, imageUrl: undefined };

  const image = post.ogImage ?? post.coverImage;
  return {
    image,
    imageUrl: image ?
    `${urls.base}${image.url}` :
    `${urls.base}/${SITE.image.path}`
  };
}